import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 800px;
  max-width: 90%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5da;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 12px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5da;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 12px;
`;

const PropertyItem = styled.div`
  background: #f6f8fa;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  position: relative;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #d1d5da;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  
  &:hover {
    background: #b1b5ba;
  }
`;

const AddButton = styled.button`
  width: 100%;
  padding: 10px;
  background: #0969da;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;
  
  &:hover {
    background: #0860ca;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #e1e4e8;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background: #28a745;
  color: white;
`;

const CancelButton = styled(Button)`
  background: #f6f8fa;
  color: #24292f;
  border: 1px solid #d1d5da;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #24292f;
  margin-bottom: 4px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
`;

function SchemaBuilderModal({ spec, onSave, onClose }) {
  const [schemaName, setSchemaName] = useState('NewSchema');
  const [properties, setProperties] = useState([
    { name: 'id', type: 'string', required: false, description: '' }
  ]);

  const addProperty = () => {
    setProperties([...properties, { name: '', type: 'string', required: false, description: '' }]);
  };

  const removeProperty = (index) => {
    setProperties(properties.filter((_, i) => i !== index));
  };

  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index][field] = value;
    setProperties(updated);
  };

  const handleSave = () => {
    try {
      const parsed = yaml.load(spec);
      
      if (!parsed.components) parsed.components = {};
      if (!parsed.components.schemas) parsed.components.schemas = {};
      
      const schema = {
        type: 'object',
        properties: {},
        required: []
      };
      
      properties.forEach(prop => {
        if (prop.name) {
          schema.properties[prop.name] = {
            type: prop.type,
            ...(prop.description && { description: prop.description })
          };
          if (prop.required) {
            schema.required.push(prop.name);
          }
        }
      });
      
      if (schema.required.length === 0) delete schema.required;
      
      parsed.components.schemas[schemaName] = schema;
      
      const newSpec = yaml.dump(parsed, { indent: 2, noRefs: true });
      onSave(newSpec);
    } catch (e) {
      alert('Error creating schema: ' + e.message);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>🛠️ Visual Schema Builder</Title>
        </Header>
        <Body>
          <Label>Schema Name</Label>
          <Input
            value={schemaName}
            onChange={(e) => setSchemaName(e.target.value)}
            placeholder="MySchema"
          />
          
          <Label style={{ marginTop: '16px' }}>Properties</Label>
          {properties.map((prop, index) => (
            <PropertyItem key={index}>
              <RemoveButton onClick={() => removeProperty(index)}>✕</RemoveButton>
              <Row>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={prop.name}
                    onChange={(e) => updateProperty(index, 'name', e.target.value)}
                    placeholder="propertyName"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={prop.type}
                    onChange={(e) => updateProperty(index, 'type', e.target.value)}
                  >
                    <option value="string">String</option>
                    <option value="integer">Integer</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="array">Array</option>
                    <option value="object">Object</option>
                  </Select>
                </div>
                <div>
                  <Label>Required</Label>
                  <input
                    type="checkbox"
                    checked={prop.required}
                    onChange={(e) => updateProperty(index, 'required', e.target.checked)}
                    style={{ marginTop: '12px' }}
                  />
                </div>
              </Row>
              <Label>Description</Label>
              <Input
                value={prop.description}
                onChange={(e) => updateProperty(index, 'description', e.target.value)}
                placeholder="Property description"
              />
            </PropertyItem>
          ))}
          <AddButton onClick={addProperty}>+ Add Property</AddButton>
        </Body>
        <ButtonGroup>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SaveButton onClick={handleSave}>Save Schema</SaveButton>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
}

export default SchemaBuilderModal;
